import { Component, Element, Prop, State, Method, Host, h, EventEmitter, Event } from '@stencil/core';
import { Color, Gesture, GestureDetail } from '../../../interface';
import { createColorClasses, getAppRoot } from '../../../utils/theme';

import { enterAnimationTop } from './animations/enter';
import { leaveAnimationTop } from './animations/leave';
import { config } from '../../../global/config';

@Component({
  tag: 'lar-notify',
  styleUrl: 'notify.scss',
  shadow: true
})

export class Notify {
  @Element() el!: HTMLLarNotifyElement;
  private config = config;

  @State() show: boolean = false;

  private gesture?: Gesture;
  private pivotY = 0;
  private timer: number = 0;

  /**
   * The icon color to use from your application's color palette.
   * Default options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color;
  /**
   * Message
   */
  @Prop({ mutable: true }) message?: string | HTMLElement | HTMLLarTranslateElement;

  /**
   * Modal z-index
   */
  @Prop() overlayIndex?: number;
  /**
   * Auto-hide timeout, will not auto-hide
   */
  @Prop({ mutable: true }) autoHide: number = 4000;

  // @ts-ignore
  private onStart(detail: GestureDetail) { // @ts-ignore
    this.pivotY = detail.currentY;
  }
  // @ts-ignore
  private onMove(detail: GestureDetail) { // @ts-ignore
    const deltaY = (detail.currentY - this.pivotY) < -10;
    if (deltaY) {
      this.dismiss();
    }
  }
  // @ts-ignore
  private onEnd(detail: GestureDetail) { // @ts-ignore

  }

  /**
   * Emitted before the notify has presented.
   */
  @Event({ eventName: 'larnotifywillpresent' }) willPresent!: EventEmitter;

  /**
   * Emitted after the modal has presented.
   */
  @Event({ eventName: 'larnotifydidpresent' }) didPresent!: EventEmitter;

  /**
   * Emitted before the modal has dismissed.
   */
  @Event({ eventName: 'larnotifywilldismiss' }) willDismiss!: EventEmitter;

  /**
   * Emitted after the modal has dismissed.
   */
  @Event({ eventName: 'larnotifydiddismiss' }) didDismiss!: EventEmitter;

  async componentDidLoad() {
    this.gesture = (await import('../../../utils/gesture')).createGesture({
      el: this.el!,
      gestureName: 'dismiss',
      gesturePriority: 100,
      direction: 'y',
      threshold: 0,
      onStart: ev => (this.onStart(ev)),
      onMove: ev => this.onMove(ev),
      onEnd: ev => this.onEnd(ev),
    });
    this.gesture.enable(true);
  }

  handleNodeClick() {
    this.dismiss();
  }

  @Method()
  async present() {
    this.willPresent.emit();
    this.show = true;
    const el = this.el.shadowRoot || this.el;
    const animation = await import('../../../utils/animation').then(mod => mod.create(enterAnimationTop, el));
    if (!this.config.getBoolean('animated', true)) {
      animation.duration(0);
    }
    await animation.playAsync();
    await animation.destroy();
    this.didPresent.emit();
    if (this.autoHide && this.autoHide > 0) {
      this.timer = window.setTimeout(() => {
        this.dismiss();
      }, this.autoHide);
    }
  }

  @Method()
  async dismiss() {
    this.willDismiss.emit();
    clearTimeout(this.timer);
    if (this.show) {
      const el = this.el.shadowRoot || this.el;
      const animation = await import('../../../utils/animation').then(mod => mod.create(leaveAnimationTop, el));
      if (!this.config.getBoolean('animated', true)) {
        animation.duration(0);
      }
      await animation.playAsync();
      await animation.destroy();
      this.show = false;
      this.didDismiss.emit()
    }
  }

  /**
   * Creates an animation instance
   */
  @Method()
  async create(message?: (string | HTMLElement | HTMLLarTranslateElement), color?: Color, autoHide?: number) {
    await this.dismiss();
    this.color = color || 'success';
    this.message = message || '';
    if (autoHide !== undefined) {
      this.autoHide = autoHide;
    }
    const doc = this.el.ownerDocument!;
    getAppRoot(doc).appendChild(this.el);
    this.present();
    if (this.el.componentOnReady) {
      await this.el.componentOnReady();
    }
  }

  render() {
    return (
    <Host
      class={{ ...createColorClasses(this.color), 'lar-notify': true }}
      style={{
        'zIndex': String(20000 + (this.overlayIndex || 0)),
        'display': (this.show?'block':'none')
      }}
    >
      <div onClick={this.handleNodeClick.bind(this)} class="lar-notify-wrapper">
        <span>{this.message}</span>
        <slot />
      </div>
    </Host>
    );
  }
}
